import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";

import "./notificationHistory.scss";
import NotificationIndicator from "./Indicator";
import Notifications from "./Notifications";
import BaseService from "../../services/BaseService";
import { setNotificationHistoryTotal, toggleNotificationHistoryStream } from "../../reducers/notificationHistory";

const LIMIT = 20;

function NotificationHistory(props) {
    const { setNotificationHistoryTotal, toggleNotificationHistoryStream, sourceState, totalHistory } = props;

    const [open, setOpen] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [notifications, setNotifications] = useState(null);
    const [notificationsLeft, setNotificationsLeft] = useState(0);
    const [refreshCount, setRefreshCount] = useState(0);

    const skipNextFetchRef = useRef(false);
    const currentStartRef = useRef(0);

    async function getData(start = 0, options = {}) {
        const { isInit } = options;
        setIsFetching(true);

        const res = await BaseService.get("get_notifications", {
            data: { limit: LIMIT, start },
        });

        if (!isInit && notifications) {
            const updated = [...notifications, ...res.result];
            setNotifications(updated);
            currentStartRef.current = start + res.result.length;
        } else {
            setNotifications(res.result);
            setNotificationHistoryTotal(res.total);
            currentStartRef.current = res.result.length;
        }

        const total = res.total;
        const current = currentStartRef.current;
        setNotificationsLeft(total - current);
        setIsFetching(false);

        return res;
    }

    async function toggleReadStatus(msgUserLogId = null, unread) {
        const data = { msgUserLogId, unread };
        const res = await BaseService.post("notification_mark_read", {
            data,
            jsonType: true,
        });

        if (res.success) {
            setNotifications(
                notifications.map(notification => {
                    if (!msgUserLogId) {
                        return { ...notification, marked_read: !msgUserLogId };
                    }
                    if (msgUserLogId === notification.msgUserLogId) {
                        return {
                            ...notification,
                            marked_read: !notification.marked_read,
                        };
                    }
                    return notification;
                })
            );
        }
    }

    async function onDelete(msgUserLogId = null, undelete) {
        const data = { msgUserLogId, undelete };
        const res = await BaseService.post("notification_mark_delete", {
            data,
            jsonType: true,
        });

        if (res.success) {
            if (msgUserLogId) {
                const updated = notifications.filter(notification => notification.msgUserLogId !== msgUserLogId);
                const newTotal = totalHistory - 1;
                const inferredLeft = newTotal - updated.length;

                setNotifications(updated);
                setNotificationHistoryTotal(newTotal);

                if (updated.length === 0 && inferredLeft > 0) {
                    setRefreshCount(prev => prev + 1);
                }
            }

            if (!msgUserLogId) {
                setNotificationHistoryTotal(0);
                setNotifications([]);
                currentStartRef.current = 0;
                setRefreshCount(prev => prev + 1);
            }
        }
    }

    function toggle() {
        setOpen(value => !value);
    }

    function close() {
        setOpen(false);
    }

    function updateStart() {
        const maxReachable = totalHistory;
        const current = currentStartRef.current;

        if (notificationsLeft > 0 && !isFetching && current < maxReachable) {
            getData(current);
        } else if (notificationsLeft > 0 && current >= maxReachable) {
            skipNextFetchRef.current = true;
            getData(0, { isInit: true }).then(res => {
                if (res?.result?.length) {
                    currentStartRef.current = res.result.length;
                }
            });
        }
    }

    useEffect(() => {
        BaseService.get("get_notifications", { data: { limit: 0, start: 0 } }).then(res => {
            setNotificationHistoryTotal(res.total);
        });
    }, []);

    useEffect(() => {
        if (!open) {
            setNotifications(null);
            currentStartRef.current = 0;
            return;
        }

        skipNextFetchRef.current = true;
        getData(0, { isInit: true });
    }, [open]);

    useEffect(() => {
        if (refreshCount === 0) return;

        skipNextFetchRef.current = true;
        getData(0, { isInit: true }).then(res => {
            if (res?.result?.length) {
                currentStartRef.current = res.result.length;
            } else {
                currentStartRef.current = 0;
            }
        });
    }, [refreshCount]);

    return (
        <div className="notification-history">
            <NotificationIndicator onClick={toggle} indicator={totalHistory} streamState={sourceState} />
            <Notifications
                close={close}
                open={open}
                notifications={notifications}
                total={totalHistory}
                toggleReadStatus={toggleReadStatus}
                onDelete={onDelete}
                onScrollDown={updateStart}
                isFetching={isFetching}
                streamState={sourceState}
                toggleNotificationHistoryStream={toggleNotificationHistoryStream}
            />
        </div>
    );
}

const mapStateToProps = ({ notificationHistory }) => ({
    totalHistory: notificationHistory.total,
    sourceState: notificationHistory.eventSourceState,
});

const mapDispatchToProps = {
    setNotificationHistoryTotal,
    toggleNotificationHistoryStream,
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationHistory);
